import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete'
import React, { useState } from 'react'
import {
  ClearButton,
  Empty,
  FieldInput,
  InputWrap,
  Item,
  List,
  Popup,
  Positioner,
  SearchIcon,
} from 'sutra/components/Autocomplete/autocomplete-styles'
import { HighlightedLabel } from 'sutra/components/Autocomplete/HighlightedLabel'
import { Close } from 'sutra/components/Icons'

export interface AutocompleteOption {
  value: string
  label: string
}

// Why the text changed: 'input-change' is typing, 'item-press' is picking a
// suggestion, 'clear-press' is the clear button.
export type AutocompleteChangeReason = BaseAutocomplete.Root.ChangeEventReason

export interface AutocompleteProps {
  options: AutocompleteOption[]
  'aria-label': string
  // Search-box mode (no `value`): called when the user picks a suggestion, by
  // click or Enter. The box is cleared afterwards; it is a way to jump
  // somewhere, not a value to keep.
  onSelect?: (option: AutocompleteOption) => void
  // Form-field mode: the controlled text of the input. Typing writes the text,
  // picking a suggestion writes its label, and nothing is cleared afterwards.
  value?: string
  onValueChange?: (value: string, reason: AutocompleteChangeReason) => void
  // Shows a clear button while the text is non-empty.
  clearable?: boolean
  // Drops the leading magnifier, for a field that is not a search box.
  hideSearchIcon?: boolean
  // Caps how many suggestions are listed.
  limit?: number
  placeholder?: string
  // Shown in the panel when nothing matches the typed text.
  emptyLabel?: string
  disabled?: boolean
  // Overlay z-index for callers embedded in their own stacking context.
  zIndex?: number
}

// A text box that suggests options as you type: the list narrows to the
// options containing the typed text, with that text picked out in each one, and
// the first match is highlighted so Enter takes it. Renders its own input and
// portals the suggestions to `document.body`, so they escape any `overflow`
// ancestor. Without `value` it is a search box that jumps (`onSelect`); with
// `value` it is a free-text field that merely suggests.
export function Autocomplete({
  options,
  onSelect,
  value,
  onValueChange,
  clearable = false,
  hideSearchIcon = false,
  limit,
  'aria-label': ariaLabel,
  placeholder = 'Search',
  emptyLabel = 'No matches',
  disabled = false,
  zIndex = 1000,
}: AutocompleteProps): React.ReactElement {
  const [query, setQuery] = useState('')
  const isField = value !== undefined
  const text = isField ? value : query

  return (
    <BaseAutocomplete.Root
      items={options}
      value={text}
      onValueChange={(next, details) => {
        onValueChange?.(next, details.reason)
        // The search box's own text; picking a suggestion would otherwise
        // write its label into it. Unused once `value` is supplied.
        if (details.reason !== 'item-press') setQuery(next)
      }}
      autoHighlight
      // antd's AutoComplete listed its suggestions on a click, so a field does
      // too; a search box waits for typing.
      openOnInputClick={isField}
      limit={limit}
      disabled={disabled}
    >
      <InputWrap>
        {!hideSearchIcon && <SearchIcon />}
        <BaseAutocomplete.Input
          render={
            <FieldInput
              $size="sm"
              $searchIcon={!hideSearchIcon}
              $clearable={clearable}
              placeholder={placeholder}
              aria-label={ariaLabel}
            />
          }
        />
        {clearable && (
          <ClearButton aria-label="Clear">
            <Close />
          </ClearButton>
        )}
      </InputWrap>
      <BaseAutocomplete.Portal>
        <Positioner sideOffset={4} $zIndex={zIndex}>
          <Popup>
            <Empty>{emptyLabel}</Empty>
            <List>
              {(option: AutocompleteOption) => (
                <Item
                  key={option.value}
                  value={option}
                  onClick={() => {
                    onSelect?.(option)
                    setQuery('')
                  }}
                >
                  <HighlightedLabel label={option.label} query={text} />
                </Item>
              )}
            </List>
          </Popup>
        </Positioner>
      </BaseAutocomplete.Portal>
    </BaseAutocomplete.Root>
  )
}

Autocomplete.displayName = 'Autocomplete'
